"use client";
import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  Box,
  Checkbox,
  Divider,
  InputAdornment,
  Skeleton,
  Typography,
  styled,
  alpha,
} from "@mui/material";
import MessageIcon from "@mui/icons-material/Message";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Coupon from "./CouponDialog";
import {
  Address,
  Customer,
  OrderServiceRequest,
  OrderSummary,
} from "@/app/classes/main";
import { ApiService } from "../../../services/AppService";
import {
  OrderRequest,
  ResponseObject,
  CalendarResponse,
  BuildTempSlot,
  ServiceHub,
} from "../../../classes/main";
import { getToken } from "../../../services/HolderService";
import { AddToCartService, Cart, MainCart } from "../../../services/cart";
import { useRouter } from "next/navigation";
import { useCurrencyCode } from "../../genericComponents/helper-functions/CurrencySymbol";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PaymentSummeryMobile from "./PaymentSummeryMobile";
import PaymentMethod from "./PaymentMethod";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { PlansPayments } from "../../plans/PlansPayment";
import toast from "react-hot-toast";
import { trackEvent } from "@/app/utils/branch";
import AddressCart from "../../address/AddressCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CircularLoader } from "../../genericComponents/loader/CircularLoader";
import { PaymentMobileLoader } from "./PaymentMobileLoader";
import {
  PAYMENT_MINT,
  PAYMENT_MINT_DARK,
  cartMobileSurfaceSx,
  paymentGradient,
  paymentGradientHover,
  paymentPrimaryButtonSx,
} from "./paymentUi";

interface PaymentMobileProps {
  setLoading: any;
  stopSetLoading: any;
  cid: any;
  updateSlotsCartMobile: any;
  setUpdateSlotsCartMobile: any;
  disposableKitCount: any;
  circularLoader: any;
  skeltonLoaderPayment: any;
  setSkeltonLoaderPayment: any;
  setCircularLoader: any;
}

interface Hub {
  id: number;
}

interface WalletAmount {
  walletAmount: number;
}

const PaymentMobile: React.FC<PaymentMobileProps> = ({
  setLoading,
  stopSetLoading,
  cid,
  updateSlotsCartMobile,
  setUpdateSlotsCartMobile,
  disposableKitCount,
  circularLoader,
  skeltonLoaderPayment,
  setSkeltonLoaderPayment,
  setCircularLoader,
}) => {
  const [openCouponDialog, setOpenCouponDialog] = React.useState(false);
  const [orderSummary, setOrderSummary] = React.useState(new OrderSummary());
  const apiService: ApiService = React.useMemo(() => new ApiService(), []);
  const [address, setAddress] = React.useState<Address | undefined>(undefined);
  const [calendarResponse, setCalendarResponse] = React.useState<
    CalendarResponse | undefined
  >(undefined);
  const [paymentMode, setPaymentMode] = React.useState<string | number>(2);
  const [paymentModeName, setPaymentModeName] = React.useState<string>(
    "Pay After Service (Cash/Online/Wallet)"
  );
  const [buildTempSlot, setBuildTempSlot] = React.useState<
    BuildTempSlot | undefined
  >(undefined);
  const [hub, setHub] = React.useState<Hub | null>(null);
  const [wallet, setWallet] = React.useState<WalletAmount | undefined>(
    undefined
  );
  const [walletRedeem, setWalletRedeem] = React.useState(false);
  const [couponCode, setCouponCode] = React.useState("");
  const [suggestion, setSuggestion] = React.useState("");
  const [suggestionAutoFocus, setSuggestionAutoFocus] = React.useState(false);
  const [couponAutoFocus, setCouponAutoFocus] = React.useState(false);
  const [showCoupon, setShowCoupon] = React.useState(false);
  const [openPaymentSummery, setOpenPaymentSummery] = React.useState(false);
  const [openPaymentMethod, setOpenPaymentMethod] = React.useState(false);
  const [openPlans, setOpenPlans] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [reloadOrderSummery, setReloadOrderSummery] = React.useState(false);
  const [reloadFlagAfterSubsciption, setReloadFlagAfterSubsciption] =
    React.useState(false);
  const router = useRouter();
  const cartService: AddToCartService = React.useMemo(
    () => new AddToCartService(),
    []
  );
  const currencyCode = useCurrencyCode();
  const token = getToken();
  const couponCodeRef = React.useRef(couponCode);
  const suggestionRef = React.useRef(suggestion);

  const handleAddOrder = async (mainCart: any, payload: any) => {
    const cart: Cart | undefined = mainCart?.cartItemRequestsById.find(
      (item: any) => item.cid === cid
    );
    const orderRequest: OrderRequest = new OrderRequest();
    orderRequest.calendarResponse = calendarResponse ?? new CalendarResponse();
    orderRequest.buildTempSlot = buildTempSlot ?? new BuildTempSlot();
    orderRequest.bookingDate = calendarResponse?.date ?? "";
    orderRequest.bookingSlot = buildTempSlot?.slot ?? "";
    orderRequest.hubId = hub?.id?.toString() ?? "";
    orderRequest.addressId = address?.id?.toString() ?? "";
    orderRequest.paymentModeCode = paymentMode ?? 2;
    orderRequest.couponCode = couponCodeRef.current;
    orderRequest.suggestion = suggestionRef.current;
    orderRequest.tempSpId = buildTempSlot?.spid ?? 0;
    orderRequest.razorpayValidatePayment = {
      orderId: payload?.orderId,
      paymentId: payload?.paymentId,
      signature: payload?.signature,
      razorpayOrder: {
        amount: payload?.razorpayOrder?.amount,
        currency: payload?.razorpayOrder?.currency,
        id: payload?.razorpayOrder?.id,
        status: payload?.razorpayOrder?.status,
      },
    };

    orderRequest.orderServiceRequests = (cart?.cartItemRequests ?? []).map(
      (item) => {
        const orderServiceRequest = new OrderServiceRequest();
        orderServiceRequest.quantity = item.count;
        orderServiceRequest.sid = item.sid;
        return orderServiceRequest;
      }
    );
    try {
      const response = await apiService.post(
        apiService.createOrder,
        orderRequest,
        getToken()!
      );
      const responseObject = response as ResponseObject;
      if (responseObject?.status == "success") {
        mainCart.cartItemRequestsById = mainCart.cartItemRequestsById.filter(
          (item: Cart) => item.cid !== cid
        );
        localStorage.setItem("cart", btoa(JSON.stringify(mainCart)));
        localStorage.setItem("count", "0");
        localStorage.setItem("oid", responseObject?.object?.oid);
        localStorage.removeItem("slot");
        router.push("/thankyou");
        stopSetLoading();
      } else {
        stopSetLoading();
        alert(`Message:" ${responseObject?.message}`);
      }
    } catch (error) {
      console.error("Error creating order:", error);
      alert("Failed to create order. Please try again.");
    }
  };

  const validatePayment = async (
    response: any,
    amount: number,
    mainCart: any
  ) => {
    try {
      const payload = {
        orderId: response.razorpay_order_id,
        paymentId: response.razorpay_payment_id,
        razorpayOrder: {
          amount: amount,
          currency: "INR",
          id: response.razorpay_order_id,
          status: "captured",
        },
        signature: response.razorpay_signature,
      };

      const validationResponse = await apiService.post<ResponseObject>(
        apiService.razorpayValidation,
        payload,
        token!
      );

      const result = await validationResponse;
      if (result?.status === "success") {
        await handleAddOrder(mainCart, payload);
      } else {
        alert("Payment verification failed.");
        stopSetLoading();
      }
    } catch (error) {
      console.error("Error validating payment:", error);
      alert("Something went wrong while verifying the payment.");
    }
  };

  const handlePayment = (
    e: any,
    orderId: string,
    amount: number,
    mainCart: any
  ) => {
    stopSetLoading();
    const url =
      "https://play-lh.googleusercontent.com/Xfvj857cLrYU8GpQczqQYldDIPXYk2ow-bCuFVWQ633NmcKKx0iZ_hC5EQM-VbXC-y97"; // Optional

    const options = {
      key: "rzp_live_OwSnlsNKdctwe8", // Replace with your Key ID
      amount: amount,
      currency: "INR",
      name: "Salon Tym",
      description: "Subscription Amount",
      image: url,
      order_id: orderId,
      handler: async (response: any) => {
        setLoading(true);
        await validatePayment(response, amount, mainCart);
      },
      theme: {
        color: "#15741a",
      },
    };
    const paymentObject = new (window as any).Razorpay(options);

    paymentObject.on("payment.failed", (response: any) => {
      alert(`Payment Failed: ${response.error.description}`);
    });

    paymentObject.open();
    e.preventDefault();
  };

  const loadRazorpaySDK = (orderId: string, amount: number, mainCart: any) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = (e) => handlePayment(e, orderId, amount, mainCart);
    document.body.appendChild(script);
  };

  const handleCreateOrder = async (amount: any, mainCart: any) => {
    setLoading(true);
    try {
      const payload = {
        amount: amount * 100,
        currency: "INR",
        receipt: "string",
      };
      const responseObject = await apiService.post<ResponseObject>(
        apiService.razorpayCreateOrder,
        payload,
        token!
      );
      if (responseObject?.status === "success") {
        const { id: orderId } = await responseObject?.object;
        loadRazorpaySDK(orderId, payload?.amount, mainCart);
      } else {
        stopSetLoading();
        alert("Failed to create order. Please try again.");
      }
    } catch (error) {
      console.error("Error validating payment:", error);
      stopSetLoading();
      alert("Something went wrong while verifying the payment.");
    }
  };

  const handleProceedButton = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    const mainCart: MainCart = cartService.getCart();
    if (!mainCart) {
      alert("No cart found.");
      return;
    }
    if (+paymentMode !== 2) {
      await handleCreateOrder(orderSummary?.finalAmount, mainCart);
    } else {
      await handleAddOrder(mainCart, null);
    }
  };

  React.useEffect(() => {
    couponCodeRef.current = couponCode;
    suggestionRef.current = suggestion;
  }, [couponCode, suggestion]);

  const initMethod =
    React.useCallback(async (): Promise<OrderSummary | null> => {
      // console.log("start logging");
      let summary: OrderSummary | null = null;
      const orderRequest: OrderRequest = new OrderRequest();
      const calendarRaw = localStorage.getItem("calendar");
      const serviceHubRaw = localStorage.getItem("serviceHub");
      const slotRaw = localStorage.getItem("slot");
      const addressRaw = localStorage.getItem("address");

      if (!calendarRaw || !serviceHubRaw || !slotRaw || !addressRaw) {
        console.log("Missing data in localStorage.");
        return null;
      }
      const calendarResponse: CalendarResponse = JSON.parse(atob(calendarRaw));
      const serviceHub: ServiceHub = JSON.parse(atob(serviceHubRaw));
      const slot: BuildTempSlot = JSON.parse(atob(slotRaw));
      const address: Address = JSON.parse(atob(addressRaw));
      setAddress(address);
      setHub(serviceHub);
      setCalendarResponse(calendarResponse);
      setBuildTempSlot(slot);
      if (!address?.id) {
        return null;
      }
      const mainCart: MainCart = cartService.getCart();
      if (!mainCart) {
        alert("No cart found.");
        return null;
      }
      const cart: Cart | undefined = mainCart?.cartItemRequestsById.find(
        (item: any) => item.cid === cid
      );
      orderRequest.calendarResponse = calendarResponse;
      orderRequest.buildTempSlot = slot;
      orderRequest.tempSpId = slot.spid;
      orderRequest.bookingDate = calendarResponse.date;
      orderRequest.walletRedeem = walletRedeem;
      orderRequest.bookingSlot = slot.slot;
      orderRequest.hubId = serviceHub.id?.toString();
      orderRequest.addressId = address?.id?.toString() ?? ""; //1729851914070
      orderRequest.suggestion = suggestionRef.current;
      orderRequest.couponCode = couponCodeRef.current;
      orderRequest.disposableKitCount = disposableKitCount;
      orderRequest.orderServiceRequests = (cart?.cartItemRequests ?? []).map(
        (item) => {
          const orderServiceRequest: OrderServiceRequest =
            new OrderServiceRequest();
          orderServiceRequest.quantity = item.count;
          orderServiceRequest.sid = item.sid;
          return orderServiceRequest;
        }
      );
      await apiService
        .post(apiService.orderSummery, orderRequest, getToken()!)
        .then((value: unknown) => {
          const orderSummaryResponse = value as ResponseObject;
          if (orderSummaryResponse?.status == "success") {
            const result = orderSummaryResponse.object as OrderSummary;
            setOrderSummary(result);
            summary = result;
          }
        });
      await apiService
        .get(apiService.customer, getToken()!)
        .then((value: unknown) => {
          const responseObject = value as ResponseObject;
          if (responseObject?.status == "success") {
            setWallet((responseObject.object as Customer).referralWallet);
            setReloadFlagAfterSubsciption(false);
          }
        });
      setUpdateSlotsCartMobile(false);
      setReloadOrderSummery(false);
      setSkeltonLoaderPayment(false);
      setCircularLoader(false);
      return summary;
    }, [
      apiService,
      cartService,
      cid,
      walletRedeem,
      reloadFlagAfterSubsciption,
      updateSlotsCartMobile,
      reloadOrderSummery,
    ]);

  React.useEffect(() => {
    initMethod();
  }, [initMethod]);

  const handleChangeCheckBox = (e: any) => {
    if (wallet && wallet?.walletAmount > 0) {
      setCircularLoader(true);
      trackEvent("WALLET_REDEEM", null);
      setWalletRedeem(e.target.checked);
      setCouponAutoFocus(false);
      setSuggestionAutoFocus(false);
    } else {
      toast.error("Insufficient Balance !", {
        duration: 5000,
        style: {
          padding: "16px",
        },
      });
    }
  };

  const handleInputChange = (event: any) => {
    setSuggestionAutoFocus(false);
    setCouponAutoFocus(true);
    setCouponCode(event.target.value);
  };
  const handleSuggestions = (event: any) => {
    setCouponAutoFocus(false);
    setSuggestionAutoFocus(true);
    setSuggestion(event.target.value);
  };
  const handleApplyCoupon = async () => {
    if (couponCodeRef.current) {
      setCircularLoader(true);
      const summary = await initMethod();
      if (summary && summary?.couponDiscount <= 0) {
        setShowCoupon(true);
        setOpenCouponDialog(true);
        trackEvent("COUPON_APPLIED", null);
      }
    } else {
      setShowCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCircularLoader(true);
    setCouponCode("");
    couponCodeRef.current = "";
    initMethod();
  };
  const handleCloseCouponDialog = () => {
    setOpenCouponDialog(false);
  };

  const handleSelectedAddress = (data: any) => {
    localStorage.setItem("address", btoa(JSON.stringify(data)));
    setAddress(data);
    setOpen(false);
    setReloadOrderSummery(true);
  };

  const handleScrollSlots = (name: any) => {
    const element = document.getElementById(name);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 150,
        behavior: "smooth",
      });
    }
  };

  const handleScrollAddress = (name: any) => {
    const element = document.getElementById(name);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 150,
        behavior: "smooth",
      });
    }
  };

  const Input = styled(TextField)(({ theme }) => ({
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
      display: "none",
    },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    borderRadius: "50px",
  }));

  const walletRowSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1,
  };

  const HoverSpan = styled("span")({
    cursor: "pointer",
    "&:hover": {
      color: "black",
    },
  });

  return (
    <React.Fragment>
      {!address?.id ? (
        <Box
          sx={{
            ...cartMobileSurfaceSx,
            marginTop: "1rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
              }}
              id="address-mobile-change"
            >
              <LocationOnIcon sx={{ width: "18px", color: PAYMENT_MINT }} />
              <Typography
                sx={{
                  paddingLeft: "0.5rem",
                  fontWeight: 800,
                  color: PAYMENT_MINT_DARK,
                }}
                variant="subtitle2"
              >
                Address
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
              }}
              onClick={() => setOpen(true)}
            >
              <Typography
                sx={{
                  paddingLeft: "0.5rem",
                  color: PAYMENT_MINT,
                  fontWeight: 700,
                  textDecoration: "none",
                }}
                variant="subtitle2"
              >
                Change
              </Typography>
              <KeyboardArrowRightIcon sx={{ width: "16px", color: PAYMENT_MINT }} />
            </Box>
          </Box>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#5e6d63",
              marginTop: "10px",
              fontWeight: "600",
            }}
          >
            {[address?.building, address?.locality, address?.formattedAddress]
              .filter(Boolean) // removes undefined, null, empty strings
              .join(", ")}
          </Typography>
          {!address?.id ? (
            <Typography variant="body2" sx={{ color: "red" }}>
              Please select address
            </Typography>
          ) : (
            ""
          )}
        </Box>
      ) : skeltonLoaderPayment ? (
        <PaymentMobileLoader />
      ) : (
        <Box>
          <Box
            sx={{
              overflow: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              padding: "0rem",
            }}
          >
            {/*------------- address Box----- */}
            <Box
              sx={{
                ...cartMobileSurfaceSx,
                marginTop: "1rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                  }}
                  id="address-mobile-change"
                >
                  <LocationOnIcon sx={{ width: "18px", color: PAYMENT_MINT }} />
                  <Typography
                    sx={{
                      paddingLeft: "0.5rem",
                      fontWeight: 800,
                      color: PAYMENT_MINT_DARK,
                    }}
                    variant="subtitle2"
                  >
                    Address
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "end",
                  }}
                  onClick={() => setOpen(true)}
                >
                  <Typography
                    sx={{
                      paddingLeft: "0.5rem",
                      color: PAYMENT_MINT,
                      fontWeight: 700,
                    }}
                    variant="subtitle2"
                  >
                    Change
                  </Typography>
                  <KeyboardArrowRightIcon sx={{ width: "16px", color: PAYMENT_MINT }} />
                </Box>
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#5e6d63",
                  marginTop: "10px",
                  fontWeight: "600",
                }}
              >
                {[
                  address?.building,
                  address?.locality,
                  address?.formattedAddress,
                ]
                  .filter(Boolean) // removes undefined, null, empty strings
                  .join(", ")}
              </Typography>
              {!address?.id ? (
                <Typography variant="body2" sx={{ color: "red" }}>
                  Please select address
                </Typography>
              ) : (
                ""
              )}
              <Divider
                sx={{
                  margin: "1rem auto",
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                onClick={() => setOpenPaymentSummery(true)}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                  }}
                >
                  <ReceiptLongIcon sx={{ width: "18px" }} />
                  <Typography
                    sx={{
                      paddingLeft: "0.5rem",
                      fontWeight: "bold",
                      color: "#000",
                    }}
                    variant="subtitle2"
                  >
                    <span style={{ fontWeight: "normal" }}> Total Bill</span>{" "}
                    &nbsp;
                    {currencyCode}
                    {orderSummary.finalAmount}
                  </Typography>
                </Box>
                <Box>
                  <KeyboardArrowRightIcon sx={{ color: "#6d6868" }} />
                </Box>
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#6d6868",
                  marginTop: "8px",
                  fontWeight: "bold",
                  fontSize: "13px",
                }}
              >
                Incl. taxes & charges
              </Typography>
              {!orderSummary?.customerSubscribed ? (
                <Box
                  sx={{
                    border: "1px solid #aaedaa",
                    background: "#f3fdf0",
                    borderRadius: "8px",
                    padding: "16px",
                    marginTop: "1rem",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#15741a",
                      }}
                    >
                      &#127881; Exclusive Discount Just For You!
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: "#aaedaa",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      margin: "8px auto",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#15741a",
                      }}
                      variant="body2"
                    >
                      Subscription Discount
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        color: "#15741a",
                      }}
                      variant="body1"
                    >
                      - {currencyCode}
                      {orderSummary?.subscriptionDiscount}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "12px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        marginTop: "5px",
                        color: PAYMENT_MINT,
                        fontWeight: 700,
                      }}
                    >
                      &#128176; Get Extra {currencyCode}
                      {orderSummary?.subscriptionDiscount} OFF
                    </Typography>{" "}
                    &nbsp; &nbsp;
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#fff",
                        background: paymentGradient,
                        borderRadius: "10rem",
                        padding: "6px 16px",
                        cursor: "pointer",
                        fontWeight: 700,
                        boxShadow: "0 6px 16px rgba(31, 122, 77, 0.25)",
                        "&:hover": {
                          background: paymentGradientHover,
                        },
                      }}
                      onClick={() => setOpenPlans(true)}
                    >
                      {!orderSummary?.customerSubscribed
                        ? "Apply Now"
                        : "Upgrade Now"}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                ""
              )}
            </Box>
            {/* =============================wallet box========================================= */}
            <Box
              sx={{
                ...cartMobileSurfaceSx,
                marginTop: "1rem",
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: "black", fontWeight: "bold" }}
                >
                  Wallet Balance
                </Typography>
              </Box>
              <Box
                sx={walletRowSx}
                style={{ padding: "4px", margin: "0px" }}
              >
                <Checkbox
                  sx={{ color: alpha(PAYMENT_MINT, 0.45), cursor: "pointer" }}
                  checked={walletRedeem}
                  onChange={handleChangeCheckBox}
                  color="success"
                />
                <Typography variant="body2">
                  {currencyCode}
                  {wallet
                    ? wallet.walletAmount > 0
                      ? wallet.walletAmount
                      : 0
                    : 0}{" "}
                  is available in your wallet
                </Typography>
              </Box>
              {(wallet && wallet?.walletAmount <= 0) || wallet == null ? (
                <Typography variant="body2" color="text.secondary">
                  Insufficient Balance!
                </Typography>
              ) : (
                ""
              )}
              {walletRedeem ? (
                <Box
                  sx={{
                    margin: "0rem 1rem",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "green" }}>
                    {orderSummary?.customerWalletResponse?.message}
                  </Typography>
                </Box>
              ) : (
                ""
              )}
            </Box>
            {orderSummary?.couponDiscount > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "#fff",
                  borderRadius: "50px",
                  padding: "8px 16px",
                  margin: "2rem auto 1rem",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: "#0acf0a",
                    }}
                  />
                  &nbsp; &nbsp;
                  <Typography>
                    The code has been successfully applied!
                  </Typography>
                  <Button
                    variant="text"
                    color="error"
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: "bold",
                      color: "red",
                    }}
                    onClick={handleRemoveCoupon}
                  >
                    Remove
                  </Button>
                </Box>
              </Box>
            ) : (
              <Input
                margin="dense"
                type="text"
                label="Redeem Coupon"
                value={couponCode}
                autoFocus={couponAutoFocus}
                onChange={handleInputChange}
                fullWidth
                InputProps={{
                  sx: {
                    paddingLeft: "5px",
                    borderRadius: "50px",
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <HoverSpan onClick={handleApplyCoupon}>Apply</HoverSpan>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  sx: {
                    typography: "caption",
                    paddingLeft: "10px",
                    "&.MuiInputLabel-shrink": {
                      paddingRight: "5px",
                      backgroundColor: "white",
                      color: "#005200",
                    },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#005200",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#005200",
                      borderRadius: "50px",
                    },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.2) ",
                  },
                  padding: "0px",
                  margin: "2rem auto 1rem",
                }}
              />
            )}
            {showCoupon
              ? orderSummary.couponDiscount <= 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      margin: "-5px 0px 12px 1rem",
                      color: "red",
                    }}
                  >
                    Coupon Not Valid
                  </Typography>
                )
              : ""}
            <Input
              margin="dense"
              type="text"
              label="Any Suggestions?"
              autoFocus={suggestionAutoFocus}
              value={suggestion}
              onChange={handleSuggestions}
              fullWidth
              InputProps={{
                sx: {
                  paddingLeft: "5px",
                  borderRadius: "50px",
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <MessageIcon sx={{ color: "#005200" }} />
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                sx: {
                  typography: "caption",
                  paddingLeft: "10px",
                  "&.MuiInputLabel-shrink": {
                    paddingRight: "5px",
                    backgroundColor: "white",
                    color: "#005200",
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#005200",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "#005200",
                    borderRadius: "50px",
                  },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(0, 0, 0, 0.2) ",
                },
                padding: "0px",
                margin: "0rem auto 1rem",
              }}
            />
          </Box>
          <Box
            sx={{
              background: "#fff",
              position: "fixed",
              bottom: 0,
              left: 0,
              zIndex: 1100,
              width: "100%",
              padding: "0.5rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              onClick={() => setOpenPaymentSummery(true)}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "start",
                  alignItems: "center",
                }}
              >
                <ReceiptLongIcon sx={{ width: "18px" }} />
                <Typography
                  sx={{
                    paddingLeft: "0.5rem",
                    fontWeight: "bold",
                    color: "#000",
                    display: "flex",
                    justifyContent: "left",
                    alignItems: "center",
                  }}
                  variant="subtitle2"
                >
                  <span style={{ fontWeight: "normal" }}> Total Bill</span>{" "}
                  &nbsp;
                  {circularLoader ? (
                    <CircularLoader />
                  ) : (
                    <>
                      {currencyCode}
                      {orderSummary.finalAmount}
                    </>
                  )}
                </Typography>
              </Box>
              <Box>
                <KeyboardArrowRightIcon sx={{ color: "#6d6868" }} />
              </Box>
            </Box>
            <Divider
              sx={{
                margin: "0.5rem auto",
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              onClick={() => handleScrollSlots("slots-mobile-view")}
            >
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                  }}
                >
                  <CalendarMonthIcon sx={{ width: "18px" }} />
                  <Typography
                    color="text.secondary"
                    sx={{
                      paddingLeft: "0.5rem",
                    }}
                    variant="subtitle2"
                  >
                    Selected date and time
                  </Typography>
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#000",
                    marginTop: "8px",
                    fontWeight: "bold",
                  }}
                >
                  {skeltonLoaderPayment ? (
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height={20}
                      animation="wave"
                    />
                  ) : (
                    <>
                      {calendarResponse?.dayShort}, {calendarResponse?.day}{" "}
                      {calendarResponse?.monthString} | {buildTempSlot?.slot}
                    </>
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ textDecoration: "underline" }}
                >
                  Change
                </Typography>
              </Box>
            </Box>
            <Divider
              sx={{
                margin: "0.5rem auto",
              }}
            />
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1.5, sm: 1 },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <Box
                sx={{
                  display: "inline-block",
                  cursor: "pointer",
                  py: 0.5,
                }}
                onClick={() => {
                  setOpenPaymentMethod(true);
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                  }}
                >
                  <Typography variant="body2">
                    Choose payment mode
                    <ExpandLessIcon sx={{ marginLeft: "4px" }} />
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {paymentModeName}
                </Typography>
              </Box>
              &nbsp; &nbsp;
              {circularLoader ? (
                <Box
                  sx={{
                    minWidth: { xs: "100%", sm: 200 },
                    flex: { xs: "1 1 100%", sm: "0 0 auto" },
                    height: "48px",
                    border: `2px solid ${alpha(PAYMENT_MINT, 0.35)}`,
                    borderRadius: "999px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    background: alpha(PAYMENT_MINT, 0.06),
                  }}
                >
                  <CircularLoader />
                </Box>
              ) : (
                <Button
                  variant="contained"
                  type="submit"
                  sx={{
                    ...paymentPrimaryButtonSx,
                    flex: { xs: "1 1 100%", sm: "0 0 auto" },
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: 200 },
                    fontSize: "1rem",
                    textTransform: "none",
                  }}
                  onClick={
                    address?.id
                      ? handleProceedButton
                      : () => handleScrollAddress("address-mobile-change")
                  }
                >
                  Book now
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
      <Coupon
        openCouponDialog={openCouponDialog}
        handleCloseCouponDialog={handleCloseCouponDialog}
      />
      <PaymentSummeryMobile
        drawerOpen={openPaymentSummery}
        closeDrawer={() => setOpenPaymentSummery(false)}
        orderSummary={orderSummary}
        setReloadFlagAfterSubsciption={setReloadFlagAfterSubsciption}
      />
      <PaymentMethod
        drawerOpen={openPaymentMethod}
        closeDrawer={() => setOpenPaymentMethod(false)}
        setPaymentMode={setPaymentMode}
        paymentMode={paymentMode}
        setPaymentModeName={setPaymentModeName}
      />
      <PlansPayments
        openPlans={openPlans}
        closePlans={() => {
          setOpenPlans(false);
        }}
        reloadFlagAfterSubsciption={() => setReloadFlagAfterSubsciption(true)}
      />
      <AddressCart
        open={open}
        handleCloseAddressDialog={() => setOpen(false)}
        isAddressFromProductCart={true}
        handleOpenProductConfirmDialog={handleSelectedAddress}
        addressForSave={address}
      />
    </React.Fragment>
  );
};

export default PaymentMobile;
